// @flow

import getPinSourceText from 'app/common/react/components/PinRep/utils/getPinSourceText';
import getPinTitle from 'app/common/react/components/PinRep/utils/getPinTitle';
import getPinTypes from 'app/common/react/components/PinRep/utils/getPinTypes';
import { type DetailedBoard } from 'app/packages/pinterest-flow-types/board';
import { type NumberOfLines } from 'app/common/react/components/PinRep/components/Footer/Title';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { type Surface } from 'app/common/react/components/PinRep/core/PinRepConfiguration';
import getAttributionType, {
  type AttributionOption,
  type AttributionType,
} from 'app/common/react/components/PinRep/utils/getAttributionType';
import {
  type FeedbackData,
  type FeedbackType,
} from 'app/common/react/components/PinRep/feedback-types';

export type BoardActivityOptions = 'default' | 'no-reply';

type BuildFooterPropsArgs = {|
  pin: Pin,

  // Option to show/hide attributions
  attributionOption: ?AttributionOption,

  // What board is displaying this PinRep, if applicable?
  board: ?DetailedBoard,

  // For changes to default board activity behavior
  boardActivityOptions?: BoardActivityOptions,

  feedbackData: FeedbackData,

  // Indicates why a pin appears in a given grid
  feedbackType: FeedbackType,

  hasCarouselData: boolean,

  inChangeTitlesExp: boolean,

  // Is this being diplayed on a group board?
  isGroupBoard: boolean,

  isLegoGroupBoardsEnabled: boolean,

  // Is this being displayed on the viewer's own board?
  isOwnBoard: boolean,
  showAvatarAndNameOnLeft: boolean,

  // Should show board activity comments
  showBoardActivityComments: boolean,

  // showImageOnly allows us to limit controls and descriptions for NUX.
  showImageOnly: boolean,

  // What grid is showing this PinRep?
  surface: Surface,

  viewerIsPartner: boolean,
|};

export type BuildFooterPropsOutput = {|
  attributionType: AttributionType,
  board: ?DetailedBoard,
  feedbackData: FeedbackData,
  feedbackType: FeedbackType,
  // Determines whether a promoted pin is from a user with an account. Used to determine whether or not to show AvatarHoverCard.
  isDarkPromoter?: boolean,
  pinTitle: string,
  showAvatarAndNameOnLeft: boolean,
  showBoardActivityComments: boolean,
  showBoardActivityReply: boolean,
  showCarouselEllipsis: boolean,
  showGroupBoardAvatar: boolean,
  showOverflowMenu: boolean,
  // This partner-only prop shows analytics on a pin
  showPinStats: boolean,
  showReactions: boolean,
  showSource: boolean,
  singleLineMaxLength: number,
  titleLines: NumberOfLines,
  useLegoPadding: boolean,
  userNote: ?string,
|};

// This function takes _descriptive_ props and outputs _imperative_ props describing the PinRep Footer.
// It does not handle what specific pin data values we show. The Footer can handle that.
// pinTitle is the exception to ☝️, as it needs to take the surface into account.
export default function buildFooterProps({
  attributionOption,
  board,
  boardActivityOptions,
  feedbackData,
  feedbackType,
  hasCarouselData,
  inChangeTitlesExp,
  isGroupBoard,
  isLegoGroupBoardsEnabled,
  isOwnBoard,
  pin,
  showAvatarAndNameOnLeft,
  showBoardActivityComments,
  showImageOnly,
  surface,
  viewerIsPartner,
}: BuildFooterPropsArgs): BuildFooterPropsOutput {
  const { isPromoted } = getPinTypes(pin);
  const { promoter, unified_user_note: unifiedUserNote } = pin;

  // Attribution may be hidden
  const attributionType = getAttributionType({
    attributionOption,
    feedbackType,
    isGroupBoard,
    pin,
    showImageOnly,
    surface,
  });

  const pinTitle = showImageOnly
    ? ''
    : getPinTitle({ inChangeTitlesExp, isGroupBoard, pin, surface });
  const showSource = !isPromoted && !showImageOnly && Boolean(getPinSourceText(pin));

  const userNote =
    inChangeTitlesExp && surface === 'BaseBoardPinGrid' && !isPromoted && unifiedUserNote
      ? unifiedUserNote.trim()
      : null;

  const showOverflowMenu = isPromoted;

  let titleLines = 2;
  if (isPromoted) {
    titleLines = 3;
  } else if (showSource || attributionType !== 'none') {
    titleLines = 1;
  }

  let singleLineMaxLength = isGroupBoard ? 25 : 30;
  // PinRep in CloseupRelatedProducts is more narrow than PinRep
  // in group borads, so the line max length is shorter
  if (['BrioCloseupRelatedProducts', 'CloseupRelatedProducts'].includes(surface)) {
    singleLineMaxLength = 23;
  }

  let showPinStats = false;
  if (surface === 'BaseBoardPinGrid') {
    showPinStats = isOwnBoard && viewerIsPartner;
  } else if (surface === 'UserProfilePinGrid') {
    showPinStats = viewerIsPartner;
  }

  // Group board props
  const showGroupBoardAvatar = isGroupBoard;
  const showReactions = Boolean(board && board.should_show_board_activity) || isGroupBoard;
  const showBoardActivityReply = boardActivityOptions !== 'no-reply';

  const isDarkPromoter = !isPromoted && !!promoter;

  const useLegoPadding = isLegoGroupBoardsEnabled || showBoardActivityComments;

  return {
    attributionType,
    feedbackData,
    feedbackType,
    isDarkPromoter,
    pinTitle,
    showAvatarAndNameOnLeft,
    showBoardActivityComments,
    showBoardActivityReply,
    showCarouselEllipsis: hasCarouselData,
    showGroupBoardAvatar,
    showOverflowMenu,
    showPinStats,
    showReactions,
    showSource,
    singleLineMaxLength,
    titleLines,
    useLegoPadding,
    userNote,
    board,
  };
}
